import React, { useState, useEffect, useRef } from "react";
import { Auth } from "aws-amplify";
import { useNavigate, useLocation } from "react-router-dom";
import { FaEye, FaEyeSlash, FaEnvelope, FaLock } from "react-icons/fa";
import DigitInputs from "../../components/ui/DigitsInputs/DigitsInputs";
import MfaChallengeStep from "./mfaChallenge/MfaChallengeStep";
import { useMfaChallenge, SOFTWARE_TOKEN_MFA } from "./mfaChallenge/useMfaChallenge";

import "../../styles/sass/features/auth/auth.scss";
import "../../styles/sass/features/auth/register.scss";

const SESSION_EXPIRED_MESSAGE = "Your sign-in session expired. Please sign in again.";
const UNSUPPORTED_CHALLENGE_MESSAGE = "This sign-in method is not supported yet. Please contact support.";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const _rawRedirect = new URLSearchParams(location.search).get("redirect");
  const redirect = _rawRedirect ? decodeURIComponent(_rawRedirect) : null;

  const [forgotPassword, setForgotPassword] = useState(false);
  const [confirmCode, setConfirmCode] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [username, setUsername] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const inputRef = useRef([]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();

        if (redirect) {
          window.location.href = redirect;
          return;
        }

        const group = user.attributes["custom:group"];

        if (group === "Host") navigate("/hostdashboard");
        else navigate("/guestdashboard");
      } catch {}
    };

    checkAuth();
  }, [navigate, redirect]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const completeSignIn = () => {
    globalThis.dispatchEvent(new Event("authChanged"));

    if (redirect) {
      window.location.href = redirect;
    } else {
      globalThis.location.reload();
    }
  };

  const returnToPasswordForm = (message) => {
    setFormData((currentFormData) => ({ ...currentFormData, password: "" }));
    setErrorMessage(message);
  };

  const {
    pendingUser,
    startChallenge,
    verifyCode,
    cancelChallenge,
    isVerifying,
    errorMessage: mfaErrorMessage,
  } = useMfaChallenge({
    onSuccess: completeSignIn,
    onSessionExpired: () => returnToPasswordForm(SESSION_EXPIRED_MESSAGE),
  });

  const handleCancelChallenge = () => {
    cancelChallenge();
    returnToPasswordForm("");
  };

  const handleSignIn = async () => {
    if (isSigningIn) {
      return;
    }

    setErrorMessage("");
    setIsSigningIn(true);
    try {
      const user = await Auth.signIn(formData.email, formData.password);

      if (user?.challengeName === SOFTWARE_TOKEN_MFA) {
        startChallenge(user);
        setIsSigningIn(false);
        return;
      }

      if (user?.challengeName) {
        setErrorMessage(UNSUPPORTED_CHALLENGE_MESSAGE);
        setIsSigningIn(false);
        return;
      }

      completeSignIn();
    } catch {
      setErrorMessage("Invalid email or password");
      setIsSigningIn(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleSignIn();
  };

  const handleRegisterClick = () => {
    const redirectForRegister =
      _rawRedirect || encodeURIComponent(location.pathname + location.search);
    navigate(`/register?redirect=${redirectForRegister}`);
  };

  const startPasswordRecovery = () => {
    setFormData((currentFormData) => ({ ...currentFormData, password: "" }));
    setErrorMessage("");
    setForgotPassword(true);
  };

  const handlePasswordRecovery = async () => {
    try {
      const response = await fetch(
        "https://mncmdr9bol.execute-api.eu-north-1.amazonaws.com/default/GetUserIDUsingEmail",
        {
          method: "POST",
          body: JSON.stringify({ email: formData.email }),
          headers: { "Content-type": "application/json" },
        }
      );

      const data = await response.json();
      setUsername(data.body);

      await Auth.forgotPassword(data.body);

      setForgotPassword(false);
      setConfirmCode(true);
    } catch {
      setErrorMsg("User not found");
    }
  };

  const submitCodeAndPassword = async () => {
    let code = "";
    inputRef.current.forEach((input) => {
      code += input.value;
    });

    try {
      await Auth.forgotPasswordSubmit(username, code, formData.password);
      alert("Password updated successfully");
      setConfirmCode(false);
    } catch (err) {
      setErrorMessage(err.message);
    }
  };

  if (pendingUser) {
    return (
      <div className="authPage">
        <div className="authCard">
          <MfaChallengeStep
            onVerify={verifyCode}
            onCancel={handleCancelChallenge}
            isVerifying={isVerifying}
            errorMessage={mfaErrorMessage}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="authPage">
      <div className="authCard">
        {forgotPassword ? (
          <>
            <h2 className="title">Recover Password</h2>

            <div className="inputGroup">
              <div className="iconBox">
                <FaEnvelope />
              </div>
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            {errorMsg && <div className="error">{errorMsg}</div>}

            <button className="primaryBtn" onClick={handlePasswordRecovery}>
              Send Code
            </button>

            <button className="secondaryBtn" onClick={() => setForgotPassword(false)}>
              Back
            </button>
          </>
        ) : confirmCode ? (
          <>
            <h2 className="title">Reset Password</h2>

            <DigitInputs amount={6} inputRef={inputRef} />

            <div className="inputGroup">
              <div className="iconBox">
                <FaLock />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="New password"
                value={formData.password}
                onChange={handleChange}
              />
              <button
                type="button"
                className="eyeIcon"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEye /> : <FaEyeSlash />}
              </button>
            </div>

            {errorMessage && <div className="error">{errorMessage}</div>}

            <button className="primaryBtn" onClick={submitCodeAndPassword}>
              Confirm
            </button>
          </>
        ) : (
          <>
            <h2 className="title">Welcome back</h2>

            <form onSubmit={handleSubmit}>
              <div className="inputGroup">
                <div className="iconBox">
                  <FaEnvelope />
                </div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isSigningIn}
                />
              </div>

              <div className="inputGroup">
                <div className="iconBox">
                  <FaLock />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isSigningIn}
                />
                <button
                  type="button"
                  className="eyeIcon"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  disabled={isSigningIn}
                >
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>

              <button
                type="button"
                className="forgotText"
                onClick={startPasswordRecovery}
                disabled={isSigningIn}
              >
                Forgot password?
              </button>

              {errorMessage && <div className="error">{errorMessage}</div>}

              <button
                type="submit"
                className="primaryBtn"
                disabled={isSigningIn}
                aria-busy={isSigningIn}
              >
                {isSigningIn ? (
                  <span className="buttonLoadingContent">
                    <span className="buttonSpinner" aria-hidden="true"></span>
                    <span>Signing in...</span>
                  </span>
                ) : (
                  "Login"
                )}
              </button>
            </form>

            <div className="bottomText">Don't have an account?</div>

            <button
              className="registerBtn"
              onClick={handleRegisterClick}
              disabled={isSigningIn}
            >
              Register
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
